import {createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import * as api from "./api";
import type {Session} from "./types";

interface AuthValue {
  session: Session | null;
  checking: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({children}: {children: ReactNode}) {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {api.getSession().then(setSession).catch(() => setSession(null)).finally(() => setChecking(false));}, []);
  const signIn = useCallback(async (username: string, password: string) => {setSession(await api.login(username, password));}, []);
  const signOut = useCallback(async () => {if (session) await api.logout(session); setSession(null);}, [session]);
  const value = useMemo(() => ({session, checking, signIn, signOut}), [session, checking, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be inside AuthProvider");
  return value;
}
